/**
 * Optional owned Gaussian splat hero (Grace Cathedral path).
 * Drop a `.ksplat` / `.splat` / `.ply` next to the manifest to activate.
 * Falls back silently when the asset is missing.
 *
 * Important: Cloudflare Pages may SPA-fallback missing paths to index.html (200).
 * Always verify Content-Type / magic bytes — never treat HTML as a splat.
 */
import { Group } from "three";
import { DropInViewer, SceneRevealMode } from "@mkkellogg/gaussian-splats-3d";

export type SplatHeroHandle = {
  group: Group;
  ready: boolean;
  update: () => void;
  dispose: () => void;
};

export type SplatManifest = {
  id?: string;
  enabled?: boolean;
  format: "ksplat" | "splat" | "ply";
  path: string;
  position?: [number, number, number];
  rotation?: [number, number, number, number];
  scale?: [number, number, number];
  attribution?: string;
};

export async function loadSplatManifest(
  url: string,
): Promise<SplatManifest | null> {
  try {
    const res = await fetch(url, { cache: "no-cache" });
    if (!res.ok) return null;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("text/html")) return null;
    return (await res.json()) as SplatManifest;
  } catch {
    return null;
  }
}

/** True only if a real splat binary is present (not SPA HTML fallback). */
async function assetExists(path: string): Promise<boolean> {
  try {
    const res = await fetch(path, {
      headers: { Range: "bytes=0-63" },
      cache: "no-cache",
    });
    if (!res.ok && res.status !== 206) return false;
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("text/html") || ct.includes("application/json")) return false;
    const buf = new Uint8Array(await res.arrayBuffer());
    if (buf.length < 4) return false;
    // Reject HTML (`<!do`) even if CF lied about content-type
    if (buf[0] === 0x3c /* < */) return false;
    return true;
  } catch {
    return false;
  }
}

export async function tryLoadSplatHero(
  manifest: SplatManifest,
): Promise<SplatHeroHandle | null> {
  if (manifest.enabled === false) return null;
  if (!(await assetExists(manifest.path))) return null;

  const viewer = new DropInViewer({
    gpuAcceleratedSort: true,
    sharedMemoryForWorkers: false,
    showLoadingUI: false,
    sceneRevealMode: SceneRevealMode.Gradual,
  });

  await viewer.addSplatScene(manifest.path, {
    progressiveLoad: true,
    showLoadingUI: false,
    position: manifest.position ?? [0, 0, 0],
    rotation: manifest.rotation ?? [0, 0, 0, 1],
    scale: manifest.scale ?? [1, 1, 1],
    splatAlphaRemovalThreshold: 5,
  });

  viewer.name = `splat-hero-${manifest.id}`;
  viewer.userData = {
    kind: "splat-hero",
    id: manifest.id,
    attribution: manifest.attribution ?? "Gaussian splat capture",
  };

  return {
    group: viewer,
    ready: true,
    update() {
      /* DropInViewer sorts via onBeforeRender */
    },
    dispose() {
      viewer.dispose?.();
    },
  };
}
